import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { getCrmOrgs, getConsolidatedStats } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Buildings, Users, Folder, CurrencyDollar, CheckSquare, ArrowRight, Globe } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';

export default function OrgSelectionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { switchOrg } = useOrg();
  const [orgs, setOrgs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgsRes, statsRes] = await Promise.all([getCrmOrgs(), getConsolidatedStats()]);
        const orgsList = orgsRes.data.items || [];
        setOrgs(orgsList);

        // Build stats map
        const statsMap = {};
        for (const s of (statsRes.data.items || [])) {
          statsMap[s.org_id] = s;
        }
        setStats(statsMap);

        // If only 1 org, skip selection
        if (orgsList.length <= 1) {
          if (orgsList.length === 1) {
            localStorage.setItem('crm_org_id', orgsList[0].id);
          }
          navigate('/dashboard', { replace: true });
        }
      } catch {
        navigate('/dashboard', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleSelectOrg = (orgId) => {
    localStorage.setItem('crm_org_id', orgId);
    navigate('/dashboard', { replace: true });
    // Force reload to apply org context
    window.location.href = '/dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const roleLabels = {
    super_admin: 'Super Admin',
    direction: 'Direction',
    commercial: 'Commercial',
    comptable: 'Comptable',
    membre_externe: 'Membre',
  };

  return (
    <div className="min-h-screen bg-[#F9F8F5] flex flex-col">
      {/* Header */}
      <div className="py-6 px-8 flex items-center justify-between border-b border-[#E8E6E1] bg-white">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>CRM ESS Outremer</h1>
            <p className="text-xs text-[#5E6D66]">Bienvenue, {user?.name || user?.email}</p>
          </div>
        </div>
        <div className="flex gap-1">
          <div className="h-1 w-5 rounded-full" style={{ background: '#00AEEF' }} />
          <div className="h-1 w-5 rounded-full" style={{ background: '#80C51F' }} />
          <div className="h-1 w-5 rounded-full" style={{ background: '#EA3C3A' }} />
          <div className="h-1 w-5 rounded-full" style={{ background: '#FF8E00' }} />
          <div className="h-1 w-5 rounded-full" style={{ background: '#D5007D' }} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-3xl space-y-6 animate-fade-in">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[#1A2421] mb-2" style={{ fontFamily: 'Manrope' }} data-testid="org-selection-title">
              Choisissez une organisation
            </h2>
            <p className="text-[#5E6D66]">Vous avez acces a {orgs.length} organisations. Selectionnez celle avec laquelle vous souhaitez travailler.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="org-selection-grid">
            {orgs.map((org) => {
              const orgStats = stats[org.id] || {};
              return (
                <Card
                  key={org.id}
                  className="border-[#E8E6E1] hover:border-[#2D5A4C] hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => handleSelectOrg(org.id)}
                  data-testid={`org-card-${org.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: org.color || '#2D5A4C' }}
                        >
                          {org.name?.charAt(0) || 'O'}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>{org.name}</h3>
                          <Badge className="mt-0.5 text-[10px]" variant="outline">{roleLabels[org.user_role] || org.user_role}</Badge>
                        </div>
                      </div>
                      <ArrowRight size={18} className="text-[#E8E6E1] group-hover:text-[#2D5A4C] transition-colors mt-1" />
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-center p-2 rounded bg-[#F9F8F5]">
                        <Users size={14} className="mx-auto text-[#5E6D66] mb-1" />
                        <p className="text-xs font-bold text-[#1A2421]">{orgStats.contacts || 0}</p>
                        <p className="text-[9px] text-[#5E6D66]">Contacts</p>
                      </div>
                      <div className="text-center p-2 rounded bg-[#F9F8F5]">
                        <Folder size={14} className="mx-auto text-[#5E6D66] mb-1" />
                        <p className="text-xs font-bold text-[#1A2421]">{orgStats.dossiers || 0}</p>
                        <p className="text-[9px] text-[#5E6D66]">Dossiers</p>
                      </div>
                      <div className="text-center p-2 rounded bg-[#F9F8F5]">
                        <CurrencyDollar size={14} className="mx-auto text-[#5E6D66] mb-1" />
                        <p className="text-xs font-bold text-[#1A2421]">{orgStats.payments || 0}</p>
                        <p className="text-[9px] text-[#5E6D66]">Paiements</p>
                      </div>
                      <div className="text-center p-2 rounded bg-[#F9F8F5]">
                        <CheckSquare size={14} className="mx-auto text-[#5E6D66] mb-1" />
                        <p className="text-xs font-bold text-[#1A2421]">{orgStats.tasks || 0}</p>
                        <p className="text-[9px] text-[#5E6D66]">Taches</p>
                      </div>
                    </div>

                    {org.is_default && (
                      <Badge className="mt-3 bg-[#2D5A4C]/10 text-[#2D5A4C] text-[10px]">Organisation par defaut</Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* View all link */}
          <div className="text-center pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { navigate('/dashboard?view=consolidated'); }}
              className="text-[#5E6D66] hover:text-[#2D5A4C]"
              data-testid="org-consolidated-link"
            >
              <Globe size={14} className="mr-1.5" />
              Vue consolidee de toutes les organisations
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
